/**
 * Formatea una fecha al estilo español: `DD Mes, YYYY`.
 * Ejemplo: `5 Julio, 2024`.
 * 
 * @param {string|number|Date} time - Fecha o timestamp válido.
 * @returns {string} Fecha formateada.
 */
export const dateFormatDDmesAnio = time => {
  const date = new Date(time);
  if (isNaN(date.getTime())) {
    return 'Fecha inválida';
  }
  const formatter = new Intl.DateTimeFormat('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  
  const parts = formatter.formatToParts(date);
  const day = parts.find(p => p.type === 'day').value;
  const month = parts.find(p => p.type === 'month').value
    .replace(/^\w/, c => c.toUpperCase());
  const year = parts.find(p => p.type === 'year').value;

  return `${day} ${month}, ${year}`;
}

/**
 * Extrae el primer bloque JSON válido del texto
 */
function extractFirstJsonBlock(text) {
  const s = String(text ?? "").trim();
  const start = s.search(/[\{\[]/);
  if (start === -1) return null;

  const opener = s[start];
  const closer = opener === "{" ? "}" : "]";
  let inString = false;
  let escaped = false;
  let depth = 0;

  for (let i = start; i < s.length; i++) {
    const c = s[i];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (c === "\\") {
        escaped = true;
      } else if (c === '"') {
        inString = false;
      }
      continue;
    }

    if (c === '"') {
      inString = true;
      continue;
    }

    if (c === opener) depth++;
    if (c === closer) depth--;

    if (depth === 0) {
      return s.slice(start, i + 1);
    }
  }

  return null;
}


/**
 * Limpia y parsea un objeto que contiene JSON envuelto en markdown.
 * Usa jsonrepair para reparar JSON malformado.
 * 
 * @param {Array|Object} response - Respuesta que puede contener JSON en formato markdown.
 * @returns {Object|null} Objeto JSON parseado o null si hay error.
 */
export const cleanJsonResponse = (response) => {
  try {
    const data = Array.isArray(response) ? response[0] : response;
    const textContent = data?.text || data;
    
    if (typeof textContent !== "string") {
      console.warn('[cleanJsonResponse] Texto no es string válido');
      return null;
    }

    // Limpiar markdown
    const cleaned = textContent
      .replace(/```json\s*/g, "")
      .replace(/```\s*/g, "")
      .trim();

    // Extraer el primer bloque JSON
    const extracted = extractFirstJsonBlock(cleaned) ?? cleaned;

    // Intento 1: Parse directo
    try {
      const parsed = JSON.parse(extracted);
      return parsed;
    } catch (e) {
      console.warn('[cleanJsonResponse] falló:', e.message);
    }

  } catch (error) {
    console.error('[cleanJsonResponse] Error general:', error);
    return null;
  }
};


export function stripAccentsLower(s = "") {
  return String(s)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

// Genera:
// - norm: texto sin acentos y en minúsculas
// - map: map[normIndex] = originalIndex
export function buildNormalizedIndexMap(original = "") {
  let norm = "";
  const map = [];

  for (let i = 0; i < original.length; i++) {
    const ch = original[i];

    // Normaliza el char (puede producir 1 o más chars, ej: "á" -> "a")
    const normalized = stripAccentsLower(ch);

    // Agrega cada char normalizado y mapea su índice al índice original
    for (let k = 0; k < normalized.length; k++) {
      norm += normalized[k];
      map.push(i);
    }
  }

  return { norm, map };
}

export function tokenizeQueryAccents(query = "") {
  const normalized = stripAccentsLower(query).trim();
  // Retornar el query completo como un único token, no dividido por palabras
  return normalized.length >= 2 ? [normalized] : [];
}

export function findRanges(normText, tokens) {
  const ranges = [];

  // Buscar el query completo como una frase consecutiva
  for (const token of tokens) {
    let start = 0;

    while (start < normText.length) {
      const idx = normText.indexOf(token, start);
      if (idx === -1) break;

      const end = idx + token.length;

      // Evitar solapamientos
      const overlaps = ranges.some(r => !(end <= r.start || idx >= r.end));
      if (!overlaps) {
        ranges.push({ start: idx, end });
      }

      start = end; // siguiente búsqueda
    }
  }

  // ordenar por inicio
  ranges.sort((a, b) => a.start - b.start);
  return ranges;
}

export const generate_query = "generate_query_utv_v3";